'use client'

import { useState } from 'react'

import { faHeart } from '@fortawesome/pro-regular-svg-icons'
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useIntl } from 'react-intl'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  SpinnerDots,
  Tooltip,
  useIsPast,
  ButtonSizes,
} from '@b-stock/fe-core/bstock-react'
import { ListingDtoPricingStrategyEnum } from '@b-stock/listing-api-client'

import { useIsUserEligibleToBid } from '@/buyer-portal/components/features/listing/actions/auction/useIsUserEligibleToBid'
import {
  COMPARE_BID_SOURCE,
  COMPARE_BID_TYPE,
  useCompareBadgeProps,
} from '@/components/listings/compare/compareAnalytics'
import {
  hasTermsRestrictions,
  hasUnacceptedTermsRestrictions,
} from '@/components/listings/ListView/listingHelpers'
import { AuctionActionCellPostload } from '@/components/listings/ListView/ListViewActionPanel/AuctionActionCell/AuctionActionCellPostload'
import useToggleWatchlisting from '@/components/listings/watchlist/useToggleWatchlisting'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import acceptedPoliciesQuery from '@/queries/acceptedPoliciesQuery'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'
import { watchedStatusQuery } from '@/queries/watchlistQuery'

import * as S from './ui'

type CompareCardActionsProps = {
  listing: CombinedListingSearchResult
}

const CompareCardActions = ({ listing }: CompareCardActionsProps) => {
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const badgeProps = useCompareBadgeProps()
  const [bidAttempt, setBidAttempt] = useState(0)
  const [watchlistButton, setWatchlistButton] =
    useState<HTMLButtonElement | null>(null)

  const { data: acceptedPolicies, status } = acceptedPoliciesQuery.useQuery()

  const personId = useDecodedAccessToken()?.personId
  const isEnded = useIsPast(listing.endTime)
  const eligibleToBid = useIsUserEligibleToBid({ sellerId: listing.sellerId })
  const { data: watchedStatus } = watchedStatusQuery.useWithOpts(
    [{ personId, listingId: listing.listingId }],
    { enabled: !!personId }
  )

  const { isCurrentlyWatchlisted, isPending, toggleWatchlistListing } =
    useToggleWatchlisting({
      listingId: listing.listingId,
      dateExpires: listing.endTime.toISOString(),
      currency: listing.currency,
      isWatchlisted:
        watchedStatus?.isWatchlisted ??
        listing.originalSearchResult.watchlistStatus?.isWatchlisted ??
        false,
    })

  const awaitingPolicies = hasTermsRestrictions(listing) && status === 'pending'
  const isUnapproved = hasUnacceptedTermsRestrictions(
    listing,
    acceptedPolicies ?? null
  )

  const canBidInPlace =
    !isEnded &&
    eligibleToBid &&
    listing.is3MP &&
    listing.pricingStrategy === ListingDtoPricingStrategyEnum.Auction &&
    listing.listingType !== 'CONTRACT'

  const opensModal = !isUnapproved && canBidInPlace

  const trackCtaClick = () => {
    if (isUnapproved) {
      trackButtonClicked({
        buttonName: 'apply_to_storefront',
        storefront_name: listing.storefrontName,
        ...badgeProps(),
      })
      return
    }
    if (canBidInPlace) {
      trackButtonClicked({
        buttonName: 'bid_now',
        bid_type: COMPARE_BID_TYPE,
        source: COMPARE_BID_SOURCE,
        listing_id: listing.listingId,
        ...badgeProps(),
      })
    }
  }

  const watchlistLabel = formatMessage(
    {
      id: isCurrentlyWatchlisted
        ? 'Listings.ComparePanel.watchlistRemove'
        : 'Listings.ComparePanel.watchlist',
    },
    { title: listing.title }
  )

  const ctaLabel = formatMessage({
    id: isUnapproved
      ? 'Listings.ComparePanel.applyCta'
      : 'Listings.ComparePanel.bidCta',
  })
  const ctaAriaLabel = formatMessage(
    {
      id: isUnapproved
        ? 'Listings.ComparePanel.applyCtaLabel'
        : 'Listings.ComparePanel.bidCtaLabel',
    },
    { title: listing.title }
  )

  return (
    <S.CardActions>
      <S.WatchlistButton
        type="button"
        ref={setWatchlistButton}
        $watchlisted={isCurrentlyWatchlisted}
        disabled={isPending}
        aria-label={watchlistLabel}
        data-testid="compare-watchlist-cta"
        data-watchlisted={isCurrentlyWatchlisted}
        onClick={() => {
          trackButtonClicked({
            buttonName: 'toggle_listing_watched',
            is_selected: !isCurrentlyWatchlisted,
            listing_id: listing.listingId,
            storefront_name: listing.storefrontName,
            ...badgeProps(),
          })
          void toggleWatchlistListing()
        }}
      >
        <FontAwesomeIcon
          icon={isCurrentlyWatchlisted ? faHeartSolid : faHeart}
        />
      </S.WatchlistButton>
      <Tooltip referenceElement={watchlistButton}>
        {formatMessage({
          id: isCurrentlyWatchlisted
            ? 'Listing.watchlist.removeTooltip'
            : 'Listing.watchlist.addTooltip',
        })}
      </Tooltip>

      {awaitingPolicies ? (
        <S.BidButton
          appearance="primary"
          size={ButtonSizes.xsmall}
          disabled
          aria-busy="true"
          aria-label={formatMessage({
            id: 'Listings.ComparePanel.ctaPending',
          })}
          data-testid="compare-bid-cta-pending"
        >
          <SpinnerDots />
        </S.BidButton>
      ) : opensModal ? (
        <S.BidButton
          appearance="primary"
          size={ButtonSizes.xsmall}
          aria-label={ctaAriaLabel}
          data-testid="compare-bid-cta"
          onClick={() => {
            trackCtaClick()
            setBidAttempt((attempt) => attempt + 1)
          }}
        >
          {ctaLabel}
        </S.BidButton>
      ) : (
        <S.BidLink
          appearance="primary"
          size={ButtonSizes.xsmall}
          href={listing.auctionUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ctaAriaLabel}
          data-testid="compare-bid-cta"
          onClick={trackCtaClick}
        >
          {ctaLabel}
        </S.BidLink>
      )}

      {bidAttempt > 0 ? (
        <AuctionActionCellPostload
          key={bidAttempt}
          headless
          bidType={COMPARE_BID_TYPE}
          searchListing={listing}
        />
      ) : null}
    </S.CardActions>
  )
}

export default CompareCardActions
